import { Component, HostListener, ViewChild } from '@angular/core';
import { Navigation, NavigationExtras, Router } from '@angular/router';

import { Subject } from 'rxjs';

import { DialogService, DspxCardSetBaseFormComponent, ElementType, FormNavigationService, SpinnerService } from 'dspx-core';

import { APP_DATA_KEYS } from '../../constants/app-data.constants';

import { LoggedInUserProfile } from '../../interfaces/logged-in-user-profile.interface';
import { SetPreviousModelInputParameters } from '../../interfaces/set-previous-model-input-parameters.interface';

import { Logger } from '../../helpers/logger';
import { SharedSimplePopup } from '../../helpers/shared-simple-popup';
import { PagePath } from '../../helpers/config-data-parser';

import { ServiceWorkerService } from '../../services/service-worker.service';
import { AppDataService } from '../../services/app-data.service';
import { ResponseParserService } from '../../services/response-parser.service';
import { HtmlHelperService } from '../../services/html-helper.service';

type FileUploadQuestion = { getQuestionModels: (formModel: any) => string[] };

@Component({ template: '' })
export abstract class BaseCardSetComponent {

  @ViewChild(DspxCardSetBaseFormComponent)
  protected baseForm: DspxCardSetBaseFormComponent;

  protected isServiceWorkerReady$ = new Subject<boolean>();
  protected destroy$ = new Subject<void>(); // Used to unsubscribe from observables in the component.
  protected componentClassName: string;
  protected logger: Logger;
  protected navigation: Navigation;
  protected fileUploadQuestions: FileUploadQuestion[];
  protected ignoreCanDeactivateGuard = false;
  protected modelKeysToIgnore: string[] = [];
  protected sharedSimplePopup: SharedSimplePopup;
  protected returnActiveTab: number | null = null;
  protected returnRoute: any[] | null = null;

  protected constructor(
    protected router: Router,
    protected serviceWorkerService: ServiceWorkerService,
    protected appDataService: AppDataService,
    protected spinnerService: SpinnerService,
    protected htmlHelperService: HtmlHelperService,
    protected dialogService: DialogService,
    protected responseParserService: ResponseParserService,
    protected formNavigationService: FormNavigationService) {

    this.componentClassName = this.constructor.name;
    this.logger = new Logger(this.componentClassName, this.appDataService.getAppData(APP_DATA_KEYS.logLevel));

    this.navigation = this.router.getCurrentNavigation();

    this.appDataService.setBreadcrumbs([]);

    this.sharedSimplePopup = new SharedSimplePopup(this.dialogService);
  }

  protected async waitForServiceWorker(): Promise<{ serviceWorker: ServiceWorker, loggedInUserProfile: LoggedInUserProfile }> {

    const logPrefix = 'waitForServiceWorker';
    this.logger.info(`${logPrefix}: enter`);

    let waitResult: { serviceWorker: ServiceWorker, loggedInUserProfile: LoggedInUserProfile };

    this.spinnerService.showSpinner();

    try {
      waitResult = await this.serviceWorkerService.waitForServiceWorker();
    }
    catch (error) {
      this.logger.error(`${logPrefix}: error while waiting for service worker`, error);
      throw error;
    }
    finally {
      this.spinnerService.hideSpinner();
    }

    // This makes sure that the ngOnInit of the child components is called only after the service worker is ready
    // (assuming that the child component has *ngIf="isServiceWorkerReady$ | async").
    // We need this because one of the child components is DspxCardSetBaseFormComponent, which calls the GetFormConfiguration endpoint during ngOnInit.
    // If the call to GetFormConfiguration happens before the service worker is ready, the fetch interceptor will not trigger.
    this.isServiceWorkerReady$.next(true);
    this.isServiceWorkerReady$.complete();

    this.logger.info(`${logPrefix}: leave`, waitResult);
    return waitResult;
  }

  protected completeDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /*
   * Routes the received event to an appropriately named handler function on this component class.
   *
   * The name of the handler function is an underscore-delimited string with the following segments:
   *   $event.field?.key,
   *   $event.field?.field?.key,
   *   $event.field?.type,
   *   $event.event?.type,
   *   $event.event?.colName,
   *   $event.event?.data?.buttonType,
   *   "Clicked"
   *
   * Any segments that are undefined are omitted entirely.
   *
   * Example of a colLink click on the column "complianceCheckId" on the grid "complianceCheckListPlaceHolder":
   *   complianceCheckListPlaceholder_colLink_complianceCheckId_Clicked
   *
   * If an event handler cannot be found with the expected name, a console error is logged that mentions
   * the expected handler name.
   *
   * @param $event
   * @returns true if an event handler was found and called, false otherwise.
   */
  protected handleActionButtonClick($event: any): boolean {
    // Build the name of the handler function to call based on the event's details
    let segments = [
      $event.field?.key,
      $event.field?.field?.key,
      $event.field?.type,
      $event.event?.type,
      $event.event?.colName,
      $event.event?.data?.buttonType,
      'Clicked'
    ];

    // Remove false-y segments
    segments = segments.filter((x) => !!x);
    segments = segments.map(x => x.replace('-', ''));

    let handlerName = 'this.actionButton_' + segments.join('_');

    let handler = eval(handlerName);

    if (handler) {
      handler = handler.bind(this);

      handler($event);

      return true;
    } else {
      console.warn(`Could not find action button click handler. Expected ${handlerName}. Source event: `, $event);

      return false;
    }
  }

  protected handleDialogButtonClick($event: any, popupCode: string): boolean {
    // Build the name of the handler function to call based on the event's details
    let segments = [
      $event?.field?.key,
      $event?.event?.type,
      $event?.event?.colName,
      $event?.event?.data?.buttonType,
      $event?.field?.field?.key,
      $event?.field?.type,
      popupCode,
      'Clicked'
    ];

    // Remove false-y segments
    segments = segments.filter((x) => !!x);

    const handlerName = 'this.dialogButton_' + segments.join('_').replace('-', '');

    let handler = eval(handlerName);

    if (handler) {
      handler = handler.bind(this);

      handler($event);

      return true;
    } else {
      console.warn(`Could not find dialog button click handler. Expected ${handlerName}. Source event: `, $event);

      return false;
    }
  }

  // NOTE: this method only works for questions inside non-repeating sections inside non-repeating pages
  protected getQuestionValue(model: any, navTabNumber: number, pageNumber: number, sectionCode: string, questionCode: string): any {
    return this.responseParserService.findQuestionModel(model, navTabNumber, pageNumber, null, sectionCode, null, questionCode);
  }

  // NOTE: this method only works for questions inside non-repeating sections inside non-repeating pages
  protected setQuestionValue(model: any, navTabNumber: number, pageNumber: number, sectionCode: string, questionCode: string, newQuestionModel: any): boolean {
    return this.responseParserService.replaceQuestionModel(model, navTabNumber, pageNumber, null, sectionCode, null, questionCode, newQuestionModel);
  }

  // NOTE: this method only works for questions inside non-repeating sections inside non-repeating pages
  protected ensureQuestionValueSet(model: any, navTabNumber: number, pageNumber: number, sectionCode: string, questionCode: string, newQuestionModel: any): boolean {
    return this.responseParserService.ensureQuestionModel(model, navTabNumber, pageNumber, null, sectionCode, null, questionCode, newQuestionModel);
  }

  protected overrideHtmlTitleSetByDspxFrameworkOnFormNavigation(): void {
    this.formNavigationService.onNavigation().subscribe(event => {
      setTimeout(() => {
        this.overrideHtmlTitleSetByDspxFramework();
      })
    });
  }

  protected overrideHtmlTitleSetByDspxFramework(): void {
    this.appDataService.overrideHtmlTitleSetByDspxFramework(this.baseForm.formName);
  }

  // This ensures that a button is enabled even when the entire form is not editable.
  protected ensurePageActionButtonsAreEnabledOnFormLoad(pagePaths: PagePath[], pageActionButtonCodes: string[]): void {
    setTimeout(
      () => {
        this.htmlHelperService.ensurePageActionButtonsAreEnabled(pagePaths, pageActionButtonCodes);
      });
  }

  protected navigate = (commands: any[], ignoreUnsavedChanges = false, extras: NavigationExtras = undefined) => {
    this.ignoreCanDeactivateGuard = ignoreUnsavedChanges;
    this.router.navigate(commands, extras);
  }

  protected reloadPage(ignoreUnsavedChanges = false) {
    this.router.routeReuseStrategy.shouldReuseRoute = () => false;
    this.router.onSameUrlNavigation = 'reload';
    this.navigate([this.router.url], ignoreUnsavedChanges);
  }

  protected resetDirtyFlagAfterLoading() {
    // This is just a lame workaround to address the fact that empty forms containing repeating pages/sections,
    // if they are configured to expand the first empty element on load, then they will cause the dirty flag to be set even if the user has not touched the form yet.
    setTimeout(() => this.baseForm.form.markAsPristine(), 200);
  }

  // The purpose of this method is to try to store the current model (and mark the form as pristine) after loading the form
  // and after all initial model changes have happened.
  //
  // By default the method runs synchronously, for those scenarios when there are no asynchonous model updates happening afterwards
  // (for example, after saving a draft).
  //
  // However, for those scenarios when there are asynchronous model updates happening afterwards (for example, after the initial load),
  // this method can be configured to run asynchronously.
  // Since every form has a slightly different implementation, this method adopts a dumb "black box" strategy,
  // by simply retrying (with delays) until the model has stopped changing.
  // It retries up to maxAttempts times (default = 3), with waits of delayBetweenAttemptsMs ms (default: 100) between attempts.
  protected setPreviousModel(params?: SetPreviousModelInputParameters): void {
    this.setPreviousModelForBaseForm(this.baseForm, params);
  }
  
  protected setPreviousModelForBaseForm(baseForm: DspxCardSetBaseFormComponent, params?: SetPreviousModelInputParameters): void {
    if (!baseForm?.model) {
      return;
    }

    const getCurrentModelAsString = () => JSON.stringify(baseForm.model);

    const saveAsPreviousModel = (modelAsString: any) => {
      baseForm.form.markAsPristine()
      this.appDataService.setAppData<any>(APP_DATA_KEYS.previousModel, JSON.parse(modelAsString));
    };

    const isAsync = params?.isAsync ?? false;
    if (!isAsync) {
      saveAsPreviousModel(getCurrentModelAsString());
      return;
    }

    const maxAttempts = params?.maxAttempts ?? 3;
    const delayBetweenAttemptsMs = params?.delayBetweenAttemptsMs ?? 100;

    let previousModelAsString = JSON.stringify(baseForm.model);
    let attemptIndex = 0;
    const waitUntilModelStopsChanging = () => {
      setTimeout(
        () => {
          const currentModelAsString = getCurrentModelAsString();
          if (++attemptIndex === maxAttempts || currentModelAsString === previousModelAsString) {
            saveAsPreviousModel(currentModelAsString);
            return;
          }
          previousModelAsString = currentModelAsString;
          waitUntilModelStopsChanging();
        },
        delayBetweenAttemptsMs);
    }
    waitUntilModelStopsChanging();
  }

  // @HostListener allows us to also guard against browser refresh, close, etc.
  @HostListener('window:beforeunload', ['$event'])
  protected unloadNotification = (event: any) => {
    if (!this.canDeactivate()) {
      // When showing the "confirm" popup on navigating without using Angular routing (i.e. type another URL / close the browser / etc.),
      // some browsers (e.g. Internet Explorer, the old non-Chromium version of Edge) will use the message below,
      // whereas other browsers (e.g. Chrome, Firefox) will override it with their own message
      event.returnValue = 'WARNING: You have unsaved changes. Press Cancel to go back and save these changes, or OK to discard these changes.';
    }
  }

  private questionChangeContext: Record<string, any> = {};

  protected handleQuestionChange(
    getValue: () => any,   // function to get the current value
    onChange: () => void,  // function to call when value changes
    previousKey: string    // name of property to store previous value
  ): void {
    const currentValue = getValue();
    if (this.questionChangeContext[previousKey] !== currentValue) {
      this.questionChangeContext[previousKey] = currentValue;
      onChange();
    }
  }

  protected initialiseQuestionModel(questionKey: string, questionValue: any): void {
    this.questionChangeContext[questionKey] = questionValue;
  }

  protected reloadModel(): void {
    this.baseForm.model = { ...this.baseForm.model };
  }

  protected initialiseReturnNavigationState(): void {
    const state = this.navigation?.extras?.state;

    this.returnActiveTab = state?.activeTab ?? null;
    this.returnRoute = state?.returnRoute ?? null;
  }

  protected restoreReturnActiveTab(): void {
    if (this.returnActiveTab != null) {
      this.formNavigationService.setActiveNumber(
        ElementType.TAB,
        this.returnActiveTab
      );
    }
  }

  // returning true will navigate without confirmation
  // returning false will show a confirm alert before navigating away
  canDeactivate(): boolean {
    return this.canDeactivateBaseForm(this.baseForm);
  }

  canDeactivateBaseForm(baseForm: DspxCardSetBaseFormComponent): boolean {
    if (this.ignoreCanDeactivateGuard) {
      return true;
    }

    const previousModel = this.getPreviousModel();
    const currentModel = baseForm.model;

    if (this.objectsAreEqual(previousModel, currentModel)) {
      return true;
    }

    return !this.formHasUpdates(baseForm);
  }

  private getPreviousModel(): any {
    return this.appDataService.getAppData<any>(APP_DATA_KEYS.previousModel);
  }

  private formHasUpdates(baseForm: DspxCardSetBaseFormComponent): boolean {
    return baseForm.form.dirty || this.formHasUpdatesToFileUploadQuestions(baseForm);
  }

  // Questions of type "fileUpload" do not trigger the dirty flag of the form, so we need to check them manually.
  private formHasUpdatesToFileUploadQuestions(baseForm: DspxCardSetBaseFormComponent): boolean {
    for (const fileUploadQuestion of this.fileUploadQuestions ?? []) {
      const currentQuestionModels = fileUploadQuestion.getQuestionModels(baseForm.model);
      const previousQuestionModels = fileUploadQuestion.getQuestionModels(this.getPreviousModel());
      if (JSON.stringify(currentQuestionModels) !== JSON.stringify(previousQuestionModels)) {
        return true;
      }
    }
    return false;
  }

  private objectsAreEqual(previousModel: any, currentModel: any): boolean {
    // If only one of the two objects is false-y, return false;
    if (!currentModel != !previousModel) {
      return false;
    }

    // If both objects are false-y, return true
    if (!currentModel && !previousModel) {
      return true;
    }

    // Otherwise, compare
    const currentModelKeys = Object.keys(currentModel);

    for (const key of currentModelKeys) {
      if (key !== '$state' && key !== 'isEditing' && !this.modelKeysToIgnore.includes(key)) {
        const previousValue = previousModel[key];
        const currentValue = currentModel[key];
        const bothAreObjects = this.isObject(previousValue) && this.isObject(currentValue);
        //Using loose equality for previousValue and currentValue here so that values like "3" and 3 are treated as equal.
        if ((bothAreObjects && !this.objectsAreEqual(previousValue, currentValue)) || (!bothAreObjects && previousValue != currentValue)) {
          return false;
        }
      }
    }
    return true;
  }

  private isObject(object: any): boolean {
    return object !== null && typeof object === 'object';
  }
}
